import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Hotel Search Service
 * Otel arama API'leri için servis
 */
export const hotelSearchService = {
  /**
   * Otel otomatik tamamlama önerilerini getir
   * @param {string} term - Arama terimi (örn: "Ad")
   * @param {string} languageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * autocomplete("Ad", "tr")
   * → GET /v1/meta/autocomplete/hotelweb?term=Ad&languageCode=tr
   */
  autocomplete: (term = "", languageCode = "tr", headers = {}, hasErrorHandler = false) => {
    const queryParams = new URLSearchParams({
      term: term,
      languageCode: languageCode,
    });
    
    return request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.hotel.search.autocomplete}?${queryParams.toString()}`,
      payload: {},
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Otel arama sonuçlarını getir
   * @param {Object} params - Arama parametreleri
   * @param {number} params.scrollIndex - Scroll index (pagination için)
   * @param {number} params.page - Sayfa numarası (pagination için)
   * @param {boolean} params.isScroll - Scroll yapılıyor mu (default: false)
   * @param {Object} params.searchFilters - Arama filtreleri
   * @param {string} params.searchFilters.checkinDate - Giriş tarihi (format: "YYYY.MM.DD")
   * @param {string} params.searchFilters.checkoutDate - Çıkış tarihi (format: "YYYY.MM.DD")
   * @param {string} params.searchFilters.destination - Destinasyon (örn: "antalya")
   * @param {Object} [params.searchFilters.userFilterInput] - Kullanıcı filtreleri (opsiyonel)
   * @param {boolean} [params.searchFilters.userFilterInput.isFiltered] - Filtreleme yapıldı mı
   * @param {number[]} [params.searchFilters.userFilterInput.stars] - Yıldız filtreleri
   * @param {string[]} [params.searchFilters.userFilterInput.boards] - Pansiyon tipi filtreleri
   * @param {number} [params.searchFilters.userFilterInput.discountRate] - İndirim oranı
   * @param {boolean} [params.searchFilters.userFilterInput.isRefundable] - İade edilebilir mi
   * @param {number} [params.searchFilters.userFilterInput.order] - Sıralama
   * @param {string} [params.searchFilters.userFilterInput.rating] - Rating filtresi
   * @param {Array} params.searchFilters.occupancies - Misafir bilgileri
   * @param {Array} params.searchFilters.occupancies[].paxList - Misafir listesi
   * @param {Object} params.searchFilters.occupancies[].paxList[] - Misafir bilgisi
   * @param {number} params.searchFilters.occupancies[].paxList[].type - Tip (1: Yetişkin, 2: Çocuk)
   * @param {number} params.searchFilters.occupancies[].paxList[].count - Sayı
   * @param {number} params.searchFilters.occupancies[].paxList[].age - Yaş
   * @param {Object} [params.preferences] - Tercihler (opsiyonel)
   * @param {string} [params.preferences.language] - Dil kodu (default: "tr")
   * @param {string} [params.preferences.currency] - Para birimi (default: "TRY")
   * @param {string} [params.preferences.nationality] - Uyruk (default: "TR")
   * @param {number} [params.preferences.deviceType] - Cihaz tipi (default: 9)
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getHotels({
   *   scrollIndex: 1,
   *   page: 1,
   *   searchFilters: {
   *     checkinDate: "2025.11.21",
   *     checkoutDate: "2025.11.25",
   *     destination: "antalya",
   *     occupancies: [{
   *       paxList: [
   *         { type: 1, count: 1, age: 30 },
   *         { type: 2, count: 1, age: 13 }
   *       ]
   *     }]
   *   },
   *   preferences: {
   *     language: "tr",
   *     currency: "TRY"
   *   }
   * })
   */
  getHotels: (
    {
      scrollIndex = 1,
      page = 1,
      isScroll = false,
      searchFilters = {},
      preferences = {},
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    const {
      checkinDate,
      checkoutDate,
      destination,
      userFilterInput = null,
      occupancies = [],
    } = searchFilters;

    // Default UserFilterInput (filtreleme yapılmadıysa)
    const defaultUserFilterInput = {
      IsFiltered: false,
      Stars: [],
      Campaigns: [],
      Boards: [],
      Chains: [],
      DiscountRate: 0,
      IsRefundable: false,
      HasDiscount: false,
      Order: 0,
      Rating: "",
      Locations: [],
      Themes: [],
      Amenities: [],
      MinPrice: 0,
      MaxPrice: 0,
      IsPriceFilterActive: false,
    };

    // UserFilterInput mapping: camelCase -> PascalCase (eğer verilmişse)
    const mappedUserFilterInput = userFilterInput
      ? {
          IsFiltered: userFilterInput.isFiltered ?? defaultUserFilterInput.IsFiltered,
          Stars: userFilterInput.stars || defaultUserFilterInput.Stars,
          Campaigns: (userFilterInput.campaigns || defaultUserFilterInput.Campaigns)
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id)),
          Boards: userFilterInput.boards || defaultUserFilterInput.Boards,
          Chains: userFilterInput.chains || defaultUserFilterInput.Chains,
          DiscountRate: userFilterInput.discountRate ?? defaultUserFilterInput.DiscountRate,
          IsRefundable: userFilterInput.isRefundable ?? defaultUserFilterInput.IsRefundable,
          HasDiscount: userFilterInput.HasDiscount ?? defaultUserFilterInput.HasDiscount,
          Order: userFilterInput.order ?? defaultUserFilterInput.Order,
          Rating: (userFilterInput.rating === 0 || userFilterInput.rating === '0') ? 0 : (userFilterInput.rating || defaultUserFilterInput.Rating),
          Locations: userFilterInput.locations || defaultUserFilterInput.Locations,
          Themes: userFilterInput.themes || defaultUserFilterInput.Themes,
          Amenities: userFilterInput.amenities || defaultUserFilterInput.Amenities,
          MinPrice: userFilterInput.minPrice ?? defaultUserFilterInput.MinPrice,
          MaxPrice: userFilterInput.maxPrice ?? defaultUserFilterInput.MaxPrice,
          IsPriceFilterActive: userFilterInput.isPriceFilterActive ?? defaultUserFilterInput.IsPriceFilterActive,
        }
      : defaultUserFilterInput;

    // Preferences için default değerler
    const defaultPreferences = {
      Language: "tr",
      Currency: "TRY",
      Nationality: "TR",
      DeviceType: 9,
    };

    // API formatı: Language küçük harf (tr, en), Currency ve Nationality büyük harf (TRY, USD, TR)
    const rawLanguage = preferences.language || defaultPreferences.Language;
    const rawCurrency = preferences.currency || defaultPreferences.Currency;
    const rawNationality = preferences.nationality || defaultPreferences.Nationality;
    const mappedPreferences = {
      Language: typeof rawLanguage === "string" ? rawLanguage.toLowerCase() : defaultPreferences.Language,
      Currency: typeof rawCurrency === "string" ? rawCurrency.toUpperCase() : defaultPreferences.Currency,
      Nationality: typeof rawNationality === "string" ? rawNationality.toUpperCase() : defaultPreferences.Nationality,
      DeviceType: preferences.deviceType ?? defaultPreferences.DeviceType,
    };

    // Payload oluştur
    const payload = {
      SearchForm: {
        ScrollIndex: scrollIndex,
        IsScroll: isScroll,
        Page: page,
        SearchFilters: [
          {
            CheckinDate: checkinDate,
            CheckoutDate: checkoutDate,
            Destination: destination,
            UserFilterInput: mappedUserFilterInput,
            Occupancies: occupancies.map((occupancy) => ({
              PaxList: occupancy.paxList.map((pax) => ({
                Type: pax.type,
                Count: pax.count,
                Age: pax.age,
              })),
            })),
          },
        ],
        Preferences: mappedPreferences,
      },
    };

    // Debug: Backend'e gönderilen price değerlerini logla
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.search.getHotels,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Otel detay veya landing sayfası için veri getir
   * @param {Object} params - Parametreler
   * @param {string} params.destination - Otel slug'ı (örn: "grand-itimat-hotel")
   * @param {boolean} params.isLandingPage - Landing sayfası mı? (query varsa false, yoksa true)
   * @param {string} [params.checkinDate] - Giriş tarihi (format: "YYYY.MM.DD") - sadece detay sayfasında
   * @param {string} [params.checkoutDate] - Çıkış tarihi (format: "YYYY.MM.DD") - sadece detay sayfasında
   * @param {Array} [params.occupancies] - Misafir bilgileri - sadece detay sayfasında
   * @param {Object} [params.preferences] - Tercihler
   * @param {string} [params.preferences.language] - Dil kodu (default: "tr")
   * @param {string} [params.preferences.currency] - Para birimi (default: "TRY")
   * @param {string} [params.preferences.nationality] - Uyruk (default: "TR")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getHotelDetailOrLanding: (
    {
      destination,
      isLandingPage = false,
      checkinDate = null,
      checkoutDate = null,
      occupancies = [],
      preferences = {},
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Preferences için default değerler
    const defaultPreferences = {
      Language: "tr",
      Currency: "TRY",
      Nationality: "TR",
      DeviceType: null,
    };

    // Preferences mapping: camelCase -> PascalCase
    const mappedPreferences = {
      Language: preferences.language || defaultPreferences.Language,
      Currency: preferences.currency || defaultPreferences.Currency,
      Nationality: preferences.nationality || defaultPreferences.Nationality,
      DeviceType: preferences.deviceType ?? defaultPreferences.DeviceType,
    };

    // SearchFilters oluştur
    const searchFilter = {
      Destination: destination,
    };

    // Eğer detay sayfası ise (isLandingPage = false), tarih ve misafir bilgilerini ekle
    if (!isLandingPage && checkinDate && checkoutDate) {
      searchFilter.CheckinDate = checkinDate;
      searchFilter.CheckoutDate = checkoutDate;
      
      if (occupancies && occupancies.length > 0) {
        searchFilter.Occupancies = occupancies.map((occupancy) => ({
          PaxList: occupancy.paxList.map((pax) => ({
            Type: pax.type,
            Count: pax.count,
            Age: pax.age,
          })),
        }));
      }
    }

    // Payload oluştur
    const payload = {
      SearchForm: {
        ScrollIndex: 1,
        IsScroll: false,
        IsLandingPage: isLandingPage,
        SearchFilters: [searchFilter],
        Preferences: mappedPreferences,
      },
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.search.getHotels,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * @param {Object} favoritedHotel - buildFavoritedHotelPayload ile oluşturulmuş payload (PascalCase: HotelId, HotelSlug, HotelName, AdultCount, ChildCount, ChildrenAges, Language, Currency, Price, CheckinDate, CheckoutDate, Amenities, StarNumber, SurveyScore, BoardName, LocationName, Images)
   */
  addFavorite: (favoritedHotel = {}, headers = {}, hasErrorHandler = false) => {
    const payload = { FavoritedHotel: favoritedHotel };
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.search.addFavorite,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /* Remove Favorite start */
  removeFavorite: (
    favoriteId,
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Payload oluştur
    const payload = {
      DataBaseId: favoriteId,
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.search.removeFavorite,
      payload,
      headers,
      hasErrorHandler,
    });
  },
  /* Remove Favorite end */

  getPopularLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.search.getPopularLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Harita için otel listesi (mapsearch API)
   * @param {Object} params - Map arama parametreleri
   * @param {Object} params.boundingBox - { topLeft: { latitude, longitude }, bottomRight: { latitude, longitude } }
   * @param {string} params.checkinDate - YYYY.MM.DD
   * @param {string} params.checkoutDate - YYYY.MM.DD
   * @param {Array} params.occupancies - [{ paxList: [{ type, count, age }] }]
   * @param {Object} params.userFilterInput - (camelCase) isFiltered, stars, boards, minPrice, maxPrice, ...
   * @param {Object} params.preferences - { language, currency, nationality, deviceType }
   * @param {boolean} [params.isNewSearch=false] - "Bu bölgede ara" tetiklendiğinde true
   * @param {string} [params.destination] - Arama lokasyonu (MapSearchFilters.Destination)
   * @param {AbortSignal} [params.signal] - İptal için
   */
  getMapHotels: (
    {
      boundingBox,
      checkinDate,
      checkoutDate,
      occupancies = [],
      userFilterInput = null,
      preferences = {},
      isNewSearch = false,
      destination,
      signal,
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    const defaultUserFilterInput = {
      IsFiltered: false,
      Stars: [],
      Campaigns: [],
      Boards: [],
      Chains: [],
      DiscountRate: 0,
      IsRefundable: false,
      Order: 0,
      Rating: "",
      Locations: [],
      Themes: [],
      Amenities: [],
      MinPrice: 0,
      MaxPrice: 0,
      IsPriceFilterActive: false,
      PointFilter: "",
      HasDiscount: false,
    };

    const mappedUserFilterInput = userFilterInput
      ? {
          IsFiltered: userFilterInput.isFiltered ?? defaultUserFilterInput.IsFiltered,
          Stars: userFilterInput.stars || defaultUserFilterInput.Stars,
          Campaigns: (userFilterInput.campaigns || defaultUserFilterInput.Campaigns)
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id)),
          Boards: userFilterInput.boards || defaultUserFilterInput.Boards,
          Chains: userFilterInput.chains || defaultUserFilterInput.Chains,
          DiscountRate: userFilterInput.discountRate ?? defaultUserFilterInput.DiscountRate,
          IsRefundable: userFilterInput.isRefundable ?? defaultUserFilterInput.IsRefundable,
          Order: userFilterInput.order ?? defaultUserFilterInput.Order,
          Rating: (userFilterInput.rating === 0 || userFilterInput.rating === '0') ? 0 : (userFilterInput.rating || defaultUserFilterInput.Rating),
          Locations: userFilterInput.locations || defaultUserFilterInput.Locations,
          Themes: userFilterInput.themes || defaultUserFilterInput.Themes,
          Amenities: userFilterInput.amenities || defaultUserFilterInput.Amenities,
          MinPrice: userFilterInput.minPrice ?? defaultUserFilterInput.MinPrice,
          MaxPrice: userFilterInput.maxPrice ?? defaultUserFilterInput.MaxPrice,
          IsPriceFilterActive: userFilterInput.isPriceFilterActive ?? defaultUserFilterInput.IsPriceFilterActive,
          PointFilter: userFilterInput.pointFilter ?? defaultUserFilterInput.PointFilter,
          HasDiscount: userFilterInput.HasDiscount ?? defaultUserFilterInput.HasDiscount,
        }
      : defaultUserFilterInput;

    const defaultPreferences = {
      Language: "tr",
      Currency: "TRY",
      Nationality: "TR",
      DeviceType: 9,
    };
    const mappedPreferences = {
      Language: preferences.language || defaultPreferences.Language,
      Currency: preferences.currency || defaultPreferences.Currency,
      Nationality: preferences.nationality || defaultPreferences.Nationality,
      DeviceType: preferences.deviceType ?? defaultPreferences.DeviceType,
    };

    const payload = {
      SearchForm: {
        IsNewSearch: isNewSearch,
        MapSearchFilters: [
          {
            CheckinDate: checkinDate,
            CheckoutDate: checkoutDate,
            BoundingBox: boundingBox
              ? {
                  TopLeft: {
                    Latitude: Number(boundingBox.topLeft?.latitude),
                    Longitude: Number(boundingBox.topLeft?.longitude),
                  },
                  BottomRight: {
                    Latitude: Number(boundingBox.bottomRight?.latitude),
                    Longitude: Number(boundingBox.bottomRight?.longitude),
                  },
                }
              : undefined,
            Destination: destination ?? undefined,
            UserFilterInput: mappedUserFilterInput,
            Occupancies: occupancies.map((occupancy) => ({
              PaxList: (occupancy.paxList || []).map((pax) => ({
                Type: pax.type,
                Count: pax.count,
                Age: pax.age,
              })),
            })),
          },
        ],
        Preferences: mappedPreferences,
      },
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.search.GoogleMaps,
      payload,
      headers,
      hasErrorHandler,
      signal,
    });
  },
};
